import React, { useState, useEffect } from "react";
import { Container, Row, Col, Alert } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getDriversList,
  verifyDriver,
  clearError,
  toggleDriverStatusOptimistic,
} from "../features/driver/driverSlice";
import {
  emailIcon,
  contactNumberIcon,
  searchIcon,
  totalDriverIcon,
  activeDriverIcon,
  completedJobsIcon,
  avDriverRatingIcon,
} from "../assets";
import notFoundEmptyIcon from "../assets/images/EmptyIcons/not-found-empty.svg";
import "../css/Driver.css";
import { ShimmerBox, ShimmerStatCards } from "../components/Shimmer";
import "../css/Shimmer.css";

const Driver = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Get data from Redux store
  const { drivers, statistics, pagination, isLoading, error } = useSelector(
    (state) => state.driver,
  );

  console.log("drivers--", drivers);

  // Fetch drivers on component mount and page change
  useEffect(() => {
    dispatch(getDriversList(currentPage));
  }, [dispatch, currentPage]);

  // Force re-render when returning to this page
  useEffect(() => {
    setSearchTerm("");
  }, [location.key]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  // Stats cards with real data
  const statsCards = [
    {
      id: 1,
      title: "Total Drivers",
      value: statistics.totalDrivers || "0",
      borderColor: "#8362E5",
      icon: totalDriverIcon,
    },
    {
      id: 2,
      title: "Active Drivers",
      value: statistics.activeDrivers || "0",
      borderColor: "#FF49CD",
      icon: activeDriverIcon,
    },
    {
      id: 3,
      title: "Completed Jobs",
      value: statistics.completedJobs || "0",
      borderColor: "#FDAF22",
      icon: completedJobsIcon,
    },
    {
      id: 4,
      title: "Average Driver Rating",
      value: statistics.averageRating || "0.0",
      rating: true,
      borderColor: "#32D484",
      icon: avDriverRatingIcon,
    },
  ];

  // Handle redirect to invite driver page
  const handleAddMember = () => {
    navigate("/invite-driver");
  };

  // Handle view/edit driver - redirect to driver details
  const handleViewDriver = (driver) => {
    navigate("/driver-details", {
      state: {
        driver: {
          id: driver.ID,
          name: driver.FullName,
          phone: driver.Phone,
          email: driver.Email,
          rating: parseFloat(driver.AverageRating) || 0,
          trips: driver.TasksCompleted || 0,
          earnings: driver.TotalEarnings || "$0",
          status: driver.AccountStatus,
          isActive: driver.IsAvailable,
          profileImage: driver.ProfileImage,
        },
      },
      replace: false,
    });
  };

  // Filter drivers based on search
  const filteredDrivers = drivers.filter(
    (driver) =>
      driver.FullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.Email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.Phone?.includes(searchTerm),
  );

  // Toggle driver active status (Availability) - Optimistic Update
  const toggleDriverStatus = async (driverId, currentStatus) => {
    const newStatus = currentStatus === "APPROVED" ? "SUSPENDED" : "APPROVED";

    // Step 1: Optimistic update - UI instantly changes
    dispatch(toggleDriverStatusOptimistic({ driverId, newStatus }));

    try {
      // Step 2: API call in background
      await dispatch(
        verifyDriver({
          driverId: driverId,
          accountStatus: newStatus,
        }),
      ).unwrap();
      // Success - no need to refresh, optimistic update already done
    } catch (err) {
      // Step 3: Revert on error
      dispatch(
        toggleDriverStatusOptimistic({ driverId, newStatus: currentStatus }),
      );
    }
  };

  // Render star rating
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <span key={i} className="star-filled">
            ⭐
          </span>,
        );
      } else {
        stars.push(
          <span key={i} className="star-empty">
            ☆
          </span>,
        );
      }
    }
    return stars;
  };

  // Get status badge class
  const getStatusClass = (status) => {
    const statusMap = {
      APPROVED: "complete",
      PENDING: "pending",
      INVITED: "pending",
      UNDER_REVIEW: "pending",
      REJECTED: "rejected",
      SUSPENDED: "suspended",
      DISABLE: "suspended",
    };
    return statusMap[status] || "pending";
  };

  // Format status text
  const formatStatus = (status) => {
    return (
      status
        ?.replace("_", " ")
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ") || "Pending"
    );
  };

  return (
    <div className="driver-page">
      <Container fluid className="driver-container">
        {/* Error Alert */}
        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => dispatch(clearError())}
          >
            {error}
          </Alert>
        )}

        {/* Stats Cards */}
        <Row className="stats-row">
          {statsCards.map((card) => (
            <Col lg={3} md={6} sm={6} key={card.id} className="stat-col">
              <div
                className="driver-stat-card"
                style={{ borderLeft: `4px solid ${card.borderColor}` }}
              >
                <div className="stat-content">
                  <div className="stat-info">
                    <p className="stat-title">{card.title}</p>
                    <h2 className="stat-value">
                      {card.value}
                      {card.rating && <span className="rating-star">★</span>}
                    </h2>
                  </div>
                  <div className="stat-icon">
                    <img src={card.icon} alt={card.title} />
                  </div>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        {/* Search and Add Button */}
        <div className="search-button-section">
          <div className="search-wrapper">
            <input
              type="text"
              className="search-field"
              placeholder="Search by name, email or phone"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <img src={searchIcon} alt="Search" className="search-icon-right" />
          </div>
          <button className="add-member-button" onClick={handleAddMember}>
            ADD DRIVER +
          </button>
        </div>

        {/* Loading Shimmer */}
        {isLoading && (
          <div className="table-main-card">
            {/* Table Header Shimmer */}
            <div className="table-header-row">
              <div className="header-cell header-name">
                <ShimmerBox width="100px" height="14px" />
              </div>
              <div className="header-cell header-trips">
                <ShimmerBox width="50px" height="14px" />
              </div>
              <div className="header-cell header-earnings">
                <ShimmerBox width="70px" height="14px" />
              </div>
              <div className="header-cell header-status">
                <ShimmerBox width="60px" height="14px" />
              </div>
              <div className="header-cell header-action">
                <ShimmerBox width="60px" height="14px" />
              </div>
            </div>
            {/* Driver Rows Shimmer */}
            {[1, 2, 3, 4, 5].map((item) => (
              <div key={item} className="driver-item-row">
                <div className="driver-main-info">
                  <div className="cell cell-id">
                    <ShimmerBox width="60px" height="14px" />
                  </div>
                  <div className="cell cell-name">
                    <div className="name-wrapper">
                      <ShimmerBox
                        width="40px"
                        height="40px"
                        borderRadius="50%"
                      />
                      <ShimmerBox width="120px" height="14px" />
                    </div>
                  </div>
                  <div className="cell cell-trips">
                    <ShimmerBox width="40px" height="14px" />
                  </div>
                  <div className="cell cell-earnings">
                    <ShimmerBox width="70px" height="14px" />
                  </div>
                  <div className="cell cell-status">
                    <ShimmerBox width="80px" height="28px" borderRadius="4px" />
                  </div>
                  <div className="cell cell-action">
                    <div className="action-group">
                      <ShimmerBox
                        width="40px"
                        height="20px"
                        borderRadius="10px"
                      />
                      <ShimmerBox
                        width="60px"
                        height="32px"
                        borderRadius="6px"
                      />
                    </div>
                  </div>
                </div>
                <div className="driver-details-info">
                  <div className="contact-details">
                    <ShimmerBox width="180px" height="14px" />
                    <ShimmerBox width="120px" height="14px" />
                  </div>
                  <div className="rating-info">
                    <ShimmerBox width="100px" height="14px" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Drivers Table */}
        {!isLoading && (
          <div className="driver-table-wrapper">
            <div className="table-main-card">
              {/* Table Header */}
              <div className="table-header-row">
                <div className="header-cell header-name">Driver Name</div>
                <div className="header-cell header-trips">Trips</div>
                <div className="header-cell header-earnings">Earnings</div>
                <div className="header-cell header-status">Status</div>
                <div className="header-cell header-action">Action</div>
              </div>

              {/* Driver Items */}
              {filteredDrivers.map((driver) => (
                <div key={driver.ID} className="driver-item-row">
                  {/* Main Row */}
                  <div className="driver-main-info">
                    <div className="cell cell-name">
                      <div className="name-wrapper">
                        <div className="avatar-circle">
                          {driver.ProfileImage ? (
                            <img
                              src={driver.ProfileImage}
                              alt={driver.FullName}
                            />
                          ) : (
                            "👤"
                          )}
                        </div>
                        <div className="name-contact-stack">
                          <span className="driver-name">{driver.FullName}</span>
                          <div className="name-contact-row">
                            <span className="contact-inline">
                              {driver.Email}
                            </span>
                          </div>
                          <div className="name-contact-row">
                            <span className="contact-inline">
                              {driver.Phone}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="cell cell-trips">
                      <span className="mobile-label">Trips:</span>
                      <span className="trip-count">
                        {driver.TasksCompleted || 0}
                      </span>
                    </div>

                    <div className="cell cell-earnings">
                      <span className="mobile-label">Earnings:</span>
                      <span className="earning-amount">
                        ${driver.TotalEarnings || "0.00"}
                      </span>
                    </div>

                    <div className="cell cell-status">
                      <span className="mobile-label">Status:</span>
                      <span
                        className={`status-tag status-${getStatusClass(driver.AccountStatus)}`}
                      >
                        {formatStatus(driver.AccountStatus)}
                      </span>
                    </div>

                    <div className="cell cell-action">
                      <span className="mobile-label">Action:</span>
                      <div className="action-group">
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={driver.AccountStatus === "APPROVED"}
                            onChange={() =>
                              toggleDriverStatus(
                                driver.ID,
                                driver.AccountStatus,
                              )
                            }
                            disabled={driver.AccountStatus === "INVITED"}
                          />
                          <span className="slider"></span>
                        </label>
                        <button
                          className="view-button"
                          onClick={() => handleViewDriver(driver)}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Details Row */}
                  <div className="driver-details-info">
                    <div className="driver-location-tags">
                      {driver.AssignedBases?.length > 0 ? (
                        driver.AssignedBases.map((base) => (
                          <span
                            key={base.BaseId}
                            className="driver-location-tag"
                          >
                            {base.BaseName}
                          </span>
                        ))
                      ) : (
                        <span
                          className="driver-location-tag"
                          style={{ color: "#9ca3af" }}
                        >
                          No bases assigned
                        </span>
                      )}
                    </div>
                    <div className="rating-info">
                      <span className="rating-text">Rating:</span>
                      <div className="stars">
                        {renderStars(parseFloat(driver.AverageRating) || 0)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Empty State */}
              {filteredDrivers.length === 0 && (
                <div className="no-results-found">
                  <img
                    src={notFoundEmptyIcon}
                    alt="No Drivers"
                    className="empty-state-icon"
                  />
                  <h3 className="empty-state-title">No Drivers Found</h3>
                  <p className="empty-state-text">
                    {searchTerm
                      ? "No drivers match your search. Try a different keyword."
                      : "You haven't added any drivers yet. Click 'ADD MEMBER' to invite your first driver."}
                  </p>
                  {!searchTerm && (
                    <button
                      className="empty-state-button"
                      onClick={handleAddMember}
                    >
                      ADD YOUR FIRST DRIVER
                    </button>
                  )}
                </div>
              )}

              {/* Pagination */}
              {pagination && pagination.TotalPages > 1 && (
                <div className="drivers-pagination">
                  <button
                    className="pagination-btn"
                    disabled={!pagination.HasPrevious}
                    onClick={() => setCurrentPage((prev) => prev - 1)}
                  >
                    Previous
                  </button>
                  <span className="pagination-info">
                    Page {pagination.CurrentPage} of {pagination.TotalPages}
                  </span>
                  <button
                    className="pagination-btn"
                    disabled={!pagination.HasNext}
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </Container>
    </div>
  );
};

export default Driver;
